<template>
  <q-dialog :model-value="modelValue" persistent @update:model-value="$emit('update:modelValue', $event)">
    <q-card style="min-width: 600px">
      <q-card-section class="bg-orange text-white">
        <div class="text-h6">Edit Device Part in Registry</div>
        <div class="text-caption">
          Update this part's specifications - changes will affect all devices using this part
        </div>
      </q-card-section>

      <q-card-section>
        <div class="q-gutter-md">
          <!-- Warning Banner -->
          <q-banner class="bg-orange-1 text-orange-9" rounded>
            <template #avatar>
              <q-icon name="warning" color="orange" />
            </template>
            <div class="text-weight-medium">
              This will affect all devices using this part
            </div>
          </q-banner>

          <q-input
            v-model="partForm.part_number"
            label="Part Number"
            outlined
            dense
            readonly
            hint="Part number cannot be changed"
          />

          <q-input
            v-model="partForm.manufacturer"
            label="Manufacturer"
            outlined
            dense
          />

          <q-input
            v-model="partForm.model"
            label="Model"
            outlined
            dense
          />

          <q-select
            v-model="partForm.brick_class"
            label="BRICK Class"
            :options="brickClassOptions"
            outlined
            dense
            emit-value
            map-options
            :loading="loadingHierarchy"
            hint="Select BRICK class (changing this will update available specification fields)"
            @update:model-value="handleBrickClassChange"
          >
            <template #prepend>
              <q-icon name="account_tree" />
            </template>
            <template #option="scope">
              <q-item v-bind="scope.itemProps">
                <q-item-section>
                  <q-item-label>{{ scope.opt.label }}</q-item-label>
                  <q-item-label caption>{{ scope.opt.description }}</q-item-label>
                </q-item-section>
              </q-item>
            </template>
          </q-select>

          <q-expansion-item
            label="Specifications"
            icon="settings"
            default-opened
          >
            <q-card>
              <q-card-section>
                <div v-if="loadingSpecFields" class="text-center q-pa-md">
                  <q-spinner color="primary" size="40px" />
                  <div class="text-caption q-mt-sm">Loading specification fields...</div>
                </div>
                <div v-else-if="specificationFields.length === 0" class="text-center text-grey-6 q-pa-md">
                  Select a BRICK class to see relevant specification fields
                </div>
                <div v-else class="q-gutter-md">
                  <template v-for="field in specificationFields" :key="field.key">
                    <!-- Text and Number fields -->
                    <q-input
                      v-if="field.type === 'text' || field.type === 'number'"
                      v-model="partForm.specifications[field.key]"
                      :label="field.label"
                      :type="field.type"
                      outlined
                      dense
                      :hint="field.hint"
                    />

                    <!-- Textarea fields -->
                    <q-input
                      v-else-if="field.type === 'textarea'"
                      v-model="partForm.specifications[field.key]"
                      :label="field.label"
                      type="textarea"
                      rows="3"
                      outlined
                      dense
                      :hint="field.hint"
                    />

                    <!-- Select dropdown fields -->
                    <q-select
                      v-else-if="field.type === 'select'"
                      v-model="partForm.specifications[field.key]"
                      :label="field.label"
                      :options="field.options"
                      outlined
                      dense
                      emit-value
                      map-options
                      clearable
                      :hint="field.hint"
                    />

                    <!-- Dependent select fields (signal_subtype depends on signal_type) -->
                    <q-select
                      v-else-if="field.type === 'select_dependent'"
                      v-model="partForm.specifications[field.key]"
                      :label="field.label"
                      :options="getDependentOptions(field)"
                      outlined
                      dense
                      emit-value
                      map-options
                      clearable
                      :disable="!partForm.specifications[field.depends_on]"
                      :hint="field.hint"
                    />
                  </template>
                </div>
              </q-card-section>
            </q-card>
          </q-expansion-item>
        </div>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn flat label="Cancel" @click="handleCancel" />
        <q-btn
          color="orange"
          label="Update Part"
          :loading="updating"
          :disable="!partForm.manufacturer || !partForm.model || !partForm.brick_class"
          @click="handleUpdate"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup>
  import { ref, watch } from 'vue';
  import { useQuasar } from 'quasar';
  import { api } from '@/services/api';

  const props = defineProps({
    modelValue: {
      type: Boolean,
      required: true,
    },
    partId: {
      type: Number,
      default: null,
    },
  });

  const emit = defineEmits(['update:modelValue', 'updated']);

  const $q = useQuasar();

  // Form state
  const partForm = ref({
    id: null,
    part_number: '',
    manufacturer: '',
    model: '',
    brick_class: '',
    specifications: {},
  });

  const brickClassOptions = ref([]);
  const specificationFields = ref([]);
  const loadingHierarchy = ref(false);
  const loadingSpecFields = ref(false);
  const updating = ref(false);

  // Watch dialog open and load part data
  watch(() => props.modelValue, async (isOpen) => {
    if (isOpen && props.partId) {
      await loadPartData();
    }
  }, { immediate: true });

  const loadPartData = async () => {
    if (!props.partId) {return;}

    try {
      const response = await api.get(`/device-parts/${props.partId}`);
      const part = response.data || response;

      // Pre-fill the form
      partForm.value = {
        id: part.id,
        part_number: part.part_number,
        manufacturer: part.manufacturer,
        model: part.model,
        brick_class: part.brick_class,
        specifications: { ...(part.specifications || {}) },
      };

      // Load BRICK hierarchy and spec fields
      if (part.brick_class) {
        await loadBrickHierarchy(part.brick_class);
        await loadSpecificationFields(part.brick_class);
      }
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to load device part details',
      });
    }
  };

  const loadBrickHierarchy = async (brickClass) => {
    if (!brickClass) {return;}

    loadingHierarchy.value = true;
    try {
      const response = await api.get('/brick-schema/hierarchy', {
        brick_class: brickClass,
      });

      const hierarchy = response.data || response || [];

      // Transform hierarchy into dropdown options with descriptions
      brickClassOptions.value = hierarchy.map((cls, index) => {
        let description = '';
        if (index === 0) {
          description = 'Most specific - exact match for this device';
        } else if (index === hierarchy.length - 1) {
          description = 'Most general - works for any related device';
        } else {
          description = `Parent class (level ${index})`;
        }

        return {
          label: cls,
          value: cls,
          description: description,
        };
      });
    } catch (error) {
      // Fallback to just the current class
      brickClassOptions.value = [{
        label: brickClass,
        value: brickClass,
        description: 'Current device class',
      }];
    } finally {
      loadingHierarchy.value = false;
    }
  };

  const loadSpecificationFields = async (brickClass) => {
    if (!brickClass) {
      specificationFields.value = [];
      return;
    }

    loadingSpecFields.value = true;
    try {
      const response = await api.get('/brick-schema/specification-fields', {
        brick_class: brickClass,
      });

      specificationFields.value = response.data || response || [];

      // Initialize specifications object with empty values for all fields
      const specs = { ...partForm.value.specifications };
      specificationFields.value.forEach((field) => {
        if (!(field.key in specs)) {
          specs[field.key] = '';
        }
      });
      partForm.value.specifications = specs;
    } catch (error) {
      // Fallback to generic fields
      specificationFields.value = [
        { key: 'description', label: 'Description', type: 'textarea', hint: 'Technical specifications' },
      ];
    } finally {
      loadingSpecFields.value = false;
    }
  };

  const handleBrickClassChange = async (brickClass) => {
    if (brickClass) {
      await loadSpecificationFields(brickClass);
    }
  };

  const getDependentOptions = (field) => {
    // Get options based on the value of the field it depends on
    if (!field.depends_on || !field.options) {
      return [];
    }

    const dependsOnValue = partForm.value.specifications[field.depends_on];
    if (!dependsOnValue) {
      return [];
    }

    return field.options[dependsOnValue] || [];
  };

  const handleCancel = () => {
    emit('update:modelValue', false);
    // Reset form
    partForm.value = {
      id: null,
      part_number: '',
      manufacturer: '',
      model: '',
      brick_class: '',
      specifications: {},
    };
    brickClassOptions.value = [];
    specificationFields.value = [];
  };

  const handleUpdate = async () => {
    updating.value = true;

    try {
      // Clean up specifications - remove empty fields
      const specs = {};
      Object.entries(partForm.value.specifications).forEach(([key, value]) => {
        if (value && String(value).trim()) {
          specs[key] = value;
        }
      });

      // Convert specs object to array if empty (PHP expects array, not object)
      const specsToSend = Object.keys(specs).length > 0 ? specs : [];

      const response = await api.put(`/device-parts/${partForm.value.id}`, {
        manufacturer: partForm.value.manufacturer,
        model: partForm.value.model,
        brick_class: partForm.value.brick_class,
        specifications: specsToSend,
      });

      // Handle wrapped response
      const updatedPart = response.data || response;

      $q.notify({
        type: 'positive',
        message: `Device part "${partForm.value.part_number}" updated successfully`,
      });

      // Emit updated event with the updated part
      emit('updated', updatedPart);

      // Close dialog
      emit('update:modelValue', false);

      // Reset form
      partForm.value = {
        id: null,
        part_number: '',
        manufacturer: '',
        model: '',
        brick_class: '',
        specifications: {},
      };
      brickClassOptions.value = [];
      specificationFields.value = [];
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: error.response?.data?.message || 'Failed to update device part',
      });
    } finally {
      updating.value = false;
    }
  };
</script>
